import { LicenseCreateUpdateRequest } from '@/clients/unifiedApi';
import { ServerListViewChangeData } from '@/components/ServerListView/ServerListViewChangeData';
import { GetBillingInvoicesArgs } from '@/models/billing';
import { Application, CompanyLicense, LicenseType } from '@/models/company';
import { CreateBrandData, GetBrandsParams, InviteBrandForm, UpdateBrandData } from '@/models/settings';
import { Store, StorePayload } from '@/models/store';
import { TaxSchedulePayload } from '@/pages/Settings/Taxes/Taxes.models';
import i18n from '@/plugins/i18n';
import * as complianceApi from '@/services/api/compliance';
import * as settingsApi from '@/services/api/settings';
import { handleError } from '@/utils/errors';

import { setupSettingsState } from './settings.state';

/**
 * This was part of an old mutation from vuex.  May be a better way to do in the future if we want to iterate on it
 */
const upsertApplicationToState = (state: ReturnType<typeof setupSettingsState>, value: Application) => {
  const index = state.applications.value.findIndex((application) => application.id === value.id);

  if (index >= 0) {
    state.applications[index] = value;
  } else {
    state.applications.value.push(value);
  }
};

export const setupSettingsActions = (state: ReturnType<typeof setupSettingsState>) => {
  /**
   * Loads billing invoices from the API
   */
  const fetchBillingInvoices = async (args?: GetBillingInvoicesArgs) => {
    state.isLoadingBillingInvoices.value = true;

    try {
      return await settingsApi.getBillingInvoices(args);
    } finally {
      state.isLoadingBillingInvoices.value = false;
    }
  };

  /**
   * Loads Brands from the API; Brands that have been previously fetched
   * and cached may be loaded directly, else fallback to localStorage.
   */
  const fetchBrands = async (params: GetBrandsParams = { limit: 500 }) => {
    state.isLoadingBrands.value = true;

    const response = await settingsApi
      .getBrands(params)
      .catch((e) => handleError(e, i18n.t('notify.errorFetchBrands')));

    state.brands.value = response.results;

    state.isLoadingBrands.value = false;

    return response;
  };

  /**
   * Get a Brand
   */
  const fetchBrand = async (id: number) => {
    try {
      const brand = await settingsApi.getBrand(id);

      return brand;
    } catch (e) {
      handleError(e, i18n.t('notify.errorFetchBrands'));
    }
  };

  /**
   * Creates a new brand
   */
  const createBrand = async (brandData: CreateBrandData) => {
    try {
      const newBrand = await settingsApi.createBrand(brandData);
      state.brands.value = [...state.brands.value, newBrand];

      return newBrand;
    } catch (e) {
      handleError(e, i18n.t('notify.errorCreateBrand'));
    }
  };

  /**
   * Updates a brand
   */
  const updateBrand = async (id: string, brandData: UpdateBrandData) => {
    try {
      const brand = await settingsApi.updateBrand(id, brandData);

      return brand;
    } catch (e) {
      handleError(e, i18n.t('notify.errorUpdateBrand'));
    }
  };

  /**
   * Invite a new manager to brand
   */
  const inviteManager = async (invite: InviteBrandForm) => {
    try {
      await settingsApi.inviteBrandManager(invite);
    } catch (e) {
      handleError(e, i18n.t('notify.errorInviteManager'));
    }
  };

  /**
   * Accept a brand invitation
   */
  const acceptBrandInvitation = async (inviteId: number, masterBrandName: string) => {
    try {
      await settingsApi.acceptBrandInvitation(inviteId, masterBrandName);
    } catch (e) {
      handleError(e, i18n.t('notify.errorAcceptInvitation'));
    }
  };

  /**
   * Decline a brand invitation
   */
  const rejectBrandInvitation = async (inviteId: number) => {
    try {
      await settingsApi.rejectBrandInvitation(inviteId);
    } catch (e) {
      handleError(e, i18n.t('notify.errorDeclineInvitation'));
    }
  };

  /**
   * Loads Brand Owner Brands from the API; Brands that have been previously fetched
   * and cached may be loaded directly, else fallback to localStorage.
   */
  const fetchBrandOwnerBrands = async () => {
    const brands = await settingsApi.getBrandOwnerBrands();

    state.brands.value = brands;

    return brands;
  };

  /**
   * Fetches a paginated list of the company's tax schedules
   */
  async function fetchTaxSchedules(): Promise<void> {
    try {
      state.isLoadingTaxSchedules.value = true;

      state.paginatedTaxSchedules.value = await settingsApi.getTaxSchedules();
    } finally {
      state.isLoadingTaxSchedules.value = false;
    }
  }

  /**
   * Saves a new tax schedule
   */
  async function saveTaxSchedule(taxSchedule: TaxSchedulePayload): Promise<void> {
    try {
      await settingsApi.postTaxSchedule(taxSchedule);
    } catch (e) {
      handleError(e, i18n.t('settings.tax.notify.errorCreating'));
    }
  }

  /**
   * Deletes a tax schedule
   */
  async function deleteTaxSchedule(id: number): Promise<void> {
    try {
      await settingsApi.deleteTaxSchedule(id);
    } catch (e) {
      handleError(e, i18n.t('settings.tax.notify.errorRemoving'));
    }
  }

  /**
   * Updates a tax schedule
   */
  async function updateTaxSchedule(id: number, taxSchedule: TaxSchedulePayload): Promise<void> {
    try {
      await settingsApi.updateTaxSchedule(id, taxSchedule);
    } catch (e) {
      handleError(e, i18n.t('settings.tax.notify.errorEditing'));
    }
  }

  /**
   * Fetchs a company's licenses
   */
  const fetchLicenses = async () => {
    const response = await settingsApi.getLicenses();

    state.licenses.value = response.results;

    return response.results;
  };

  /**
   * Fetches Company Staff members.
   */
  const fetchStaff = async ({ active }: { active: boolean } = { active: false }) => {
    const data = await settingsApi.getStaff({ active });
    const staff = data.map((s) => {
      return {
        ...s,
        name: s.displayName
          ? s.isActive
            ? s.displayName
            : `${s.displayName} (${i18n.t('common.deactivated').toLowerCase()})`
          : '',
      };
    });

    state.staff.value = staff;
  };

  const fetchStaffWithPermissions = async ({ params }: { params?: ServerListViewChangeData['serialized'] } = {}) => {
    const staffMembers = await settingsApi.getStaffWithPermissions({ params });

    state.staffWithPermissions.value = staffMembers;

    return staffMembers;
  };

  const fetchStaffById = async (id: number | `${number}`) => {
    const staffMember = await settingsApi.getStaffById(id);

    return staffMember;
  };

  /**
   * Fetches Brand Manager Distributors from the API
   */
  const fetchBrandManagerDistributors = async () => {
    try {
      const response = await settingsApi.getDistributors();

      state.distributors.value = response;
    } catch (e) {
      handleError(e, i18n.t('orders.brand_owner.error_fetch_distributors'));
    }
  };

  /**
   * Fetches Company Applications
   */
  const fetchApplications = async () => {
    const { results } = await settingsApi.getApplications();

    state.applications.value = results;
  };

  /**
   * Fetches Company Applications
   */
  const createApplication = async (application: Application) => {
    const response = await settingsApi.addApplication(application);

    upsertApplicationToState(state, response);

    return response;
  };

  /**
   * Fetches Company Applications
   */
  const saveApplication = async (application: Application): Promise<void> => {
    const response = await settingsApi.saveApplication(application);

    upsertApplicationToState(state, response);

    return response;
  };

  /**
   * Fetches Company Applications
   */
  const deleteApplication = async (id): Promise<void> => {
    await settingsApi.deleteApplication(id);

    const index = state.applications.value.findIndex((application) => application.id === id);

    state.applications.value.splice(index, 1);
  };

  /**
   * Fetches Company Applications
   */
  const regenerateApplicationToken = async (id) => {
    const response = await settingsApi.regenerateApplicationToken(id);

    upsertApplicationToState(state, response);

    return response;
  };

  /**
   * Fetches all payment options
   * Included methods and terms
   */
  const fetchPaymentOptionsSettings = async () => {
    const { paymentMethods, paymentTerms } = await settingsApi.getPaymentOptionsSettings();

    state.paymentMethods.value = paymentMethods;
    state.paymentTerms.value = paymentTerms;
  };

  /**
   * Fetches array of company licenses
   */
  const fetchCompanyLicenses = async () => {
    state.isLoadingCompanyLicenses.value = true;

    const companyLicenses = await complianceApi.getCompanyLicenses();

    state.companyLicenses.value = companyLicenses;
    state.isLoadingCompanyLicenses.value = false;
  };

  /**
   * Fetches array of license types for a state
   */
  const fetchLicenseTypes = async (licenseState: LicenseType['state']) => {
    const licenseTypes = await settingsApi.getLicenseTypes(licenseState);

    state.licenseTypes.value = licenseTypes;
  };

  /**
   * Archive a company license
   * @param companyLicense - company license to archive
   */
  const archiveCompanyLicense = async (companyLicenseId: CompanyLicense['companyLicenseId']) => {
    await settingsApi.archiveCompanyLicense(companyLicenseId);
    fetchCompanyLicenses();
  };

  const createLicenseChangeRequest = async (params: FormData) => {
    await settingsApi.createLicenseChangeRequest(params);
    fetchCompanyLicenses();
  };

  /**
   * Fetchs a stores's pending requests
   */
  const fetchPendingStoreRequests = async () => {
    const pendingStoreRequests = await settingsApi.getStorePendingRequests();

    return pendingStoreRequests;
  };

  /**
   * Creates a new store
   */
  const createStore = async (storeData: StorePayload) => {
    const newStore: Store = await settingsApi
      .createStore(storeData)
      .catch((e) => handleError(e, i18n.t('notify.errorCreatingStore')));

    return newStore;
  };

  /**
   * Updates a store license document
   */
  const addStoreLicense = async (id: number, license: File) => {
    const store = await settingsApi
      .updateLicense(id, license)
      .catch((e) => handleError(e, i18n.t('notify.errorUpdateBrand')));

    return store;
  };

  const getBrandById = (brandId: number) => {
    return state.brands.value.find((brand) => brand.id === brandId);
  };

  const fetchBillingPaymentMethods = async () => {
    state.isLoadingBillingPaymentMethods.value = true;

    try {
      return await settingsApi.getBillingPaymentMethods();
    } finally {
      state.isLoadingBillingPaymentMethods.value = false;
    }
  };

  const deleteCompanyPaymentMethod = async (paymentMethodId: string) => {
    state.isLoadingBillingPaymentMethods.value = true;

    try {
      return await settingsApi.deleteCompanyPaymentMethod(paymentMethodId);
    } finally {
      state.isLoadingBillingPaymentMethods.value = false;
    }
  };

  const setCompanyPaymentMethodToDefault = async (paymentMethodId: string) => {
    state.isLoadingBillingPaymentMethods.value = true;

    try {
      return await settingsApi.setCompanyPaymentMethodToDefault(paymentMethodId);
    } finally {
      state.isLoadingBillingPaymentMethods.value = false;
    }
  };

  /**
   * Fetches array of all license types
   */
  const fetchAllLicenseTypes = async () => {
    const licenseTypes = await complianceApi.getLicenseTypes();

    return licenseTypes;
  };

  const createLicenses = async (licenses: LicenseCreateUpdateRequest[]) => {
    const response = await complianceApi.createLicenses(licenses);

    return response;
  };

  return {
    fetchAllLicenseTypes,
    fetchBrand,
    fetchBillingInvoices,
    fetchBrands,
    fetchBrandOwnerBrands,
    fetchLicenses,
    fetchStaff,
    fetchStaffById,
    fetchStaffWithPermissions,
    fetchTaxSchedules,
    fetchBrandManagerDistributors,
    fetchApplications,
    createApplication,
    createBrand,
    createLicenses,
    updateBrand,
    updateTaxSchedule,
    inviteManager,
    acceptBrandInvitation,
    rejectBrandInvitation,
    saveApplication,
    saveTaxSchedule,
    deleteApplication,
    deleteTaxSchedule,
    regenerateApplicationToken,
    fetchPaymentOptionsSettings,
    fetchCompanyLicenses,
    fetchLicenseTypes,
    archiveCompanyLicense,
    createLicenseChangeRequest,
    fetchPendingStoreRequests,
    createStore,
    addStoreLicense,
    getBrandById,
    fetchBillingPaymentMethods,
    deleteCompanyPaymentMethod,
    setCompanyPaymentMethodToDefault,
  };
};
